import { toastAlert } from "$lib/commons-web2"
import { captureException } from "@sentry/sveltekit"
import { ErrorContainer, type ErrorDetail } from "@tastyworks/tastyworks-api"
import { _, json } from "svelte-i18n"
import { get } from "svelte/store"
import type { RequireAtLeastOne } from "type-fest"

export class ErrorController {
  isTerminal: boolean = false
  toast?: () => void = undefined

  private get textBundle(): any {
    return get(json)("errors")
  }

  // Handles errors within SvelteKit's load functions and initial rendering
  static handleLoadError({ error }: { error: unknown }) {
    console.error(error)
  }

  // Handles all other synchronous and async errors
  handleError(error: unknown) {
    if (!this.isTerminal) {
      this.isTerminal = error instanceof BaseError ? !error.recoverable : true
    }
    console.error(error)

    if (error instanceof RestError) {
      this.toast = () => alertRestError(error)
    } else if (error instanceof BaseError) {
      this.toast = () =>
        toastAlert("error", this.textBundle.alertTitle, error.message)
    } else {
      this.toast = () =>
        toastAlert(
          "error",
          this.textBundle.alertTitle,
          this.textBundle.other.description
        )
    }

    this.toast()
  }

  static getRestErrorMessage(error: RestError) {
    if (error.errorContainer && !error.errorContainer.isEmpty()) {
      return error.errorContainer.toString()
    } else {
      return error.message
    }
  }
}

class CleanException extends Error {
  code?: string

  constructor(message: string) {
    super(message)
  }
}

export class ExpectedError extends Error {
  displayMessage: string
  code?: string

  constructor(
    message: string,
    options?: { code?: string; displayMessage?: string }
  ) {
    super(message)
    this.displayMessage = options?.displayMessage || message
    this.code = options?.code
  }
}

interface ReportOptions {
  alertUser?: boolean
}

export function reportAndRecover(
  error: unknown,
  { alertUser = true }: ReportOptions = {}
) {
  let alertTitle: string = get(json)("errors.alertTitle")
  let alertMsg: string = get(json)("errors.other.description")

  const cleaned = new CleanException(alertMsg)

  if (error instanceof ExpectedError) {
    alertTitle = cleaned.name = error.name
    alertMsg = error.displayMessage

    cleaned.message = error.message
    cleaned.code = error.code
  } else if (error instanceof ErrorContainer) {
    alertTitle = cleaned.name = error.message
    alertMsg = cleaned.message = error.toString()
  } else if (error instanceof Error) {
    alertTitle = error.name
    alertMsg = error.message
  }

  captureException(cleaned)
  console.error(error)
  if (alertUser) toastAlert("error", alertTitle, alertMsg)
}

abstract class TryCatchResult<T> {
  abstract readonly ok: boolean
  abstract readonly err: boolean

  abstract unwrap(): T
  abstract tap(cb: (value: T) => void): Ok<T> | Err

  abstract reportIfError(options?: ReportOptions): void
}

export class Ok<T> extends TryCatchResult<T> {
  readonly ok = true
  readonly err = false

  constructor(readonly value: T) {
    super()
  }

  override unwrap() {
    return this.value
  }

  override tap(cb: (value: T) => void) {
    cb(this.value)
    return this
  }

  override reportIfError() {
    return /* no-op */
  }
}

export class Err extends TryCatchResult<never> {
  readonly ok = false
  readonly err = true

  constructor(readonly error: unknown) {
    super()
  }

  override unwrap(): never {
    throw this.error
  }

  override tap(_cb: (value: never) => void) {
    return this
  }

  override reportIfError(options?: ReportOptions) {
    this.report(options)
  }

  report(options?: ReportOptions) {
    reportAndRecover(this.error, options)
    return this
  }
}

/**
 * Wraps an async callback in a try/catch and returns a result object instead of
 * throwing, so errors can be handled without a surrounding try/catch block.
 *
 * @param callback The async function to invoke.
 * @param args Arguments forwarded to `callback`.
 * @returns A promise resolving to `Ok<T>` (with `value`) on success, or `Err`
 * (with `error`) if the callback throws. Narrow on the `ok` discriminant.
 *
 * @example
 * const result = await tc(fetchCustomer, customerId)
 * if (result.ok) {
 *   use(result.value)
 * } else {
 *   reportAndRecover(result.error)
 * }
 */
export async function tc<T, A extends unknown[]>(
  callback: (...args: A) => Promise<T>,
  ...args: A
): Promise<Ok<T> | Err> {
  try {
    const result = await callback(...args)
    return new Ok(result)
  } catch (e) {
    return new Err(e)
  }
}

type SettledTuple<T extends Array<() => unknown>> = {
  [K in keyof T]: Ok<Awaited<ReturnType<T[K]>>> | Err
}

/**
 * Behaves exactly like `Promise.allSettled` over a list of thunks, except each
 * outcome is wrapped in `Ok`/`Err` rather than `PromiseSettledResult`, so the
 * results narrow and unwrap the same way as {@link tc}'s.
 *
 * @param callbacks The functions to invoke. Each is called with no arguments.
 * @returns A promise resolving to a tuple in the same order as `callbacks`,
 * where each entry is `Ok` (with `value`) or `Err` (with `error`)
 * Never rejects. Narrow on the `ok` discriminant.
 *
 * @example
 * const [customer, agreements] = await tcAllSettled([
 *   () => fetchCustomer(customerId),
 *   () => fetchAgreements(regulatoryDomain),
 * ])
 * if (customer.ok) {
 *   use(customer.value)
 * } else {
 *   reportAndRecover(customer.error)
 * }
 */
export async function tcAllSettled<T extends Array<() => unknown>>(
  callbacks: [...T]
): Promise<SettledTuple<T>> {
  const settledList = await Promise.allSettled(callbacks.map((cb) => cb()))
  // Promise.allSettled preserves input order and length; .map() erases that
  // from the type but not at runtime, so the assertion is sound
  return settledList.map((settled) => {
    if (settled.status === "fulfilled") return new Ok(settled.value)
    else return new Err(settled.reason)
  }) as SettledTuple<T>
}

/**
 * Synchronous counterpart to {@link tc}. Wraps a synchronous callback in a
 * try/catch and returns a result object instead of throwing.
 *
 * @param callback The function to invoke.
 * @param args Arguments forwarded to `callback`.
 * @returns `Ok<T>` (with `value`) on success, or `Err` (with `error`) if the
 * callback throws. Narrow on the `ok` discriminant.
 *
 * @example
 * const result = tcSync(JSON.parse, raw)
 * if (result.ok) {
 *   use(result.value)
 * } else {
 *   reportAndRecover(result.error)
 * }
 */
export function tcSync<T, A extends unknown[]>(
  callback: (...args: A) => T,
  ...args: A
): Ok<T> | Err {
  try {
    const result = callback(...args)
    return new Ok(result)
  } catch (e) {
    return new Err(e)
  }
}

export class AbortSubmissionError extends Error {}

interface BaseErrorParameters<T extends string> {
  name: T
  message: string
  recoverable?: boolean
}

export class BaseError<T extends string> extends Error {
  recoverable: boolean

  constructor({ name, message, recoverable }: BaseErrorParameters<T>) {
    super(message)
    this.name = name
    this.recoverable = recoverable ?? false
  }
}

type UnexpectedStateErrorParameters = Omit<
  BaseErrorParameters<"UnexpectedStateError">,
  "name"
>

/**
 * Represents a custom error class for unexpected states that may occur in the application.
 * This error should be thrown when the application encounters a state that it should not have reached.
 * It will be reported to sentry and should be fixed.
 *
 * This will be very similar in utility to `console.assert(false, "message")`.
 * The main difference is that this will break out of the current execution context and report an error to the user.
 */
export class UnexpectedStateError extends BaseError<"UnexpectedStateError"> {
  constructor(params: UnexpectedStateErrorParameters) {
    super({ name: "UnexpectedStateError", ...params })
    Error.captureStackTrace?.(this, UnexpectedStateError)
  }
}

export enum RestErrorType {
  NotFound = "Not Found",
  BadRequest = "Bad Request",
  Unauthorized = "Unauthorized",
  Forbidden = "Forbidden",
}

export type RestOperation = "create" | "read" | "update" | "delete" | "validate"

// omit name while error type is not in use
type RestErrorParameters<T extends string> = Omit<
  BaseErrorParameters<T>,
  "name" | "message"
> &
  RequireAtLeastOne<
    {
      message?: string // use as i18nPath so that it's not outright ignored
      operation: RestOperation
      resource: string
      errorContainer?: ErrorContainer
    },
    "message" | "errorContainer"
  >

type ModelValidationError = RestError & {
  errorContainer: ErrorContainer
} & { code: "validation_error" }

export class RestError extends BaseError<"RestError"> {
  operation: RestOperation
  resource: string
  errorContainer?: ErrorContainer

  /**
   * Creates a new instance of the RestError class.
   * @param operation The REST operation that caused the error.
   * @param resource The resource associated with the error.
   * @param errorContainer The error container object containing the error details.
   */
  constructor({
    operation,
    resource,
    errorContainer,
    recoverable,
    message,
  }: RestErrorParameters<RestErrorType>) {
    super({
      name: "RestError",
      message: message
        ? get(_)(message)
        : errorContainer
          ? errorContainer.message
          : "",
      recoverable,
    })
    this.operation = operation
    this.resource = resource
    this.errorContainer = errorContainer
    Error.captureStackTrace?.(this, RestError)
  }

  get validationErrors(): ErrorDetail[] {
    if (!this.isValidationError()) {
      return []
    }
    return this.errorContainer.errors
  }

  isValidationError(): this is ModelValidationError {
    return this.errorContainer?.code === "validation_error"
  }
}

export function alertRestError(error: RestError) {
  toastAlert(
    "error",
    get(_)("errors.alertTitle"),
    error.errorContainer?.message,
    error.errorContainer?.errors.map(
      (e) => e.message || `${e.domain} ${e.reason}`
    )
  )
}

export function createSessionInvalidationError() {
  return new RestError({
    operation: "validate",
    resource: "Customer",
    recoverable: true,
    message: get(_)(
      "personalInfo.affiliationInformation.sessionInvalidationMessage"
    ),
  })
}

const DUPLICATE_LOGIN_ERROR_CODES = [
  "username_taken",
  "email_taken",
  "email_and_username_taken",
]

export function isDuplicateLoginError(error: ErrorContainer) {
  return DUPLICATE_LOGIN_ERROR_CODES.includes(error.code)
}
